import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { message, notification } from 'antd';
import { AESEncrypt } from '@/utils/crypto/AESEncrypt';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const {
        success,
        data,
        code,
        message: errorMessage,
      } = res as unknown as API.Response;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode: code, errorMessage, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: any = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        if (error.response.status === 401) {
          const currentPath = history.location.pathname;
          if (currentPath !== '/login') {
            sessionStorage.removeItem('acc_token');
            history.replace('/login');
          }
          // 401 已处理，避免继续走默认提示逻辑
          return;
        }
        if (error.response.status === 400) {
          notification.error({
            description: error.response.data.message || '请求参数错误',
            message: '400',
          });
          return;
        }
        if (error.response.status === 403) {
          notification.error({
            description: '您暂无该操作的权限！',
            message: '403',
          });
          return;
        }
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (url: string, config: any) => {
      console.log(
        '[Request Interceptor] Original config:',
        JSON.stringify({
          url: url,
          method: config.method,
          params: config.params,
          data: config.data,
        }),
      );

      // 拦截请求配置，进行个性化处理。
      // 从 sessionStorage 获取 token
      const token = sessionStorage.getItem('acc_token');
      // 检查是否是登录接口
      const isLoginUrl = url?.includes('/login');
      // 除了登录接口，其他接口都添加 token 到请求头
      if (!isLoginUrl && token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }

      // ========== 所有请求参数 AES-CBC-128 加密（已关闭）==========
      // const method = config.method?.toUpperCase() || 'GET';
      //
      // if (method === 'GET' && config.params && Object.keys(config.params).length > 0) {
      //   const encrypted = AESEncrypt.encrypt(config.params);
      //   config.params = { data:encrypted };
      // } else if ((method === 'POST' || method === 'PUT' || method === 'DELETE') && config.data && Object.keys(config.data).length > 0) {
      //   const encrypted = AESEncrypt.encrypt(config.data);
      //   config.data = { data:encrypted };
      // }

      return { url, options: config };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理

      // ========== 解密响应数据（已关闭）==========
      // try {
      //   const responseData = response.data as any;
      //   // 判断是否为加密响应：data 字段存在且为字符串（Base64加密内容）
      //   if (responseData && typeof responseData.data === 'string' && responseData.data.length > 0) {
      //     const decrypted = AESEncrypt.decrypt(responseData.data);
      //     // 直接将解密后的对象替换 response.data
      //     response.data = decrypted as any;
      //   }
      // } catch (error) {
      //   // 解密失败，保持原样（可能是未加密的响应）
      //   console.log('[Response Interceptor] Decryption skipped or failed:', error);
      // }

      const data = response.data as API.Response;
      if (!data.success) {
        message.error(data.message || '请求失败！');
      }
      if (data.code === 401) {
        const currentPath = history.location.pathname;
        if (currentPath !== '/login') {
          sessionStorage.removeItem('acc_token');
          history.replace('/login');
        }
        return response;
      }

      // 检查是否是登录接口且调用成功
      if (
        response.config.url?.includes('/login') &&
        data.success &&
        data.data?.access_token
      ) {
        // 存储 token 到 sessionStorage
        sessionStorage.setItem('acc_token', data.data.access_token);
      }

      return response;
    },
  ],
};
